import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  MainLayout,
  PageLoader,
  ButtonLoader,
  TablePagination,
} from "./host";
import {
  getSavedSqlConnection,
  hasSqlSession,
  clearSqlSession,
  isSqlSessionExpiredError,
  isSqlQueryCanceledError,
  sqlCancel,
  sqlColumns,
  sqlConnect,
  sqlDatabases,
  sqlDisconnect,
  sqlHealth,
  sqlQuery,
  sqlRelatedTables,
  sqlTables,
  sqlUseDatabase,
} from "./api";
import SearchSelect from "./SearchSelect";
import { SQL_API_URL, isLocalSqlBridgeHost } from "./config";
import "./SqlStudio.css";

const SAVED_LOGIN_KEY = "sqlEasyLogin";
const ALIASES = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
const ENGINE_MSSQL = "mssql";
const ENGINE_MYSQL = "mysql";
const ROW_LIMIT_ALL = "all";
const ROW_LIMIT_OPTIONS = [
  { value: "100", label: "100 rows" },
  { value: "500", label: "500 rows" },
  { value: "1000", label: "1,000 rows" },
  { value: "5000", label: "5,000 rows" },
  { value: "10000", label: "10,000 rows" },
  { value: "50000", label: "50,000 rows" },
  { value: "100000", label: "100,000 rows" },
  { value: ROW_LIMIT_ALL, label: "All rows (no limit)" },
];

function isMysql(engine) {
  return engine === ENGINE_MYSQL;
}

function qIdent(name, engine) {
  const s = String(name ?? "");
  if (isMysql(engine)) return `\`${s.replace(/`/g, "``")}\``;
  return `[${s.replace(/]/g, "]]")}]`;
}

function qName(schema, name, engine) {
  return `${qIdent(schema, engine)}.${qIdent(name, engine)}`;
}

function qCol(alias, column, engine) {
  return `${alias}.${qIdent(column, engine)}`;
}

/** Build SELECT … FROM/WHERE with dialect-aware row limit. */
function buildSelect(selectList, fromWhereSql, limit, engine) {
  const body = fromWhereSql.trim();
  if (limit === ROW_LIMIT_ALL) {
    return `SELECT ${selectList}\n${body}`.trim();
  }
  const n = Number(limit) || 100;
  if (isMysql(engine)) {
    return `SELECT ${selectList}\n${body}\nLIMIT ${n}`.trim();
  }
  return `SELECT TOP ${n} ${selectList}\n${body}`.trim();
}

function fetchMaxRows(limit) {
  if (limit === ROW_LIMIT_ALL) return 0;
  return Math.max(1, Number(limit) || 100);
}

function likeExpr(colExpr, value, engine) {
  const safe = String(value).replace(/'/g, "''");
  if (isMysql(engine)) {
    return `CAST(${colExpr} AS CHAR) LIKE '%${safe}%'`;
  }
  return `CAST(${colExpr} AS NVARCHAR(MAX)) LIKE N'%${safe}%'`;
}

function litString(value, engine) {
  const safe = String(value).replace(/'/g, "''");
  return isMysql(engine) ? `'${safe}'` : `N'${safe}'`;
}

function engineLabel(engine) {
  return isMysql(engine) ? "MySQL" : "MS SQL Server";
}

function defaultPort(engine) {
  return isMysql(engine) ? "3306" : "1433";
}

function defaultUser(engine) {
  return isMysql(engine) ? "root" : "sa";
}

const JOIN_TYPES = [
  { id: "INNER", label: "Inner Join", help: "Only matching rows" },
  { id: "LEFT", label: "Left Join", help: "All from left table" },
  { id: "RIGHT", label: "Right Join", help: "All from right table" },
];

const WHERE_OPS = [
  { id: "=", label: "Equals" },
  { id: "<>", label: "Not equals" },
  { id: "LIKE", label: "Contains" },
  { id: ">", label: "Greater than" },
  { id: "<", label: "Less than" },
  { id: ">=", label: "Greater or equal" },
  { id: "<=", label: "Less or equal" },
  { id: "IS NULL", label: "Is empty" },
  { id: "IS NOT NULL", label: "Is not empty" },
];

function loadSavedLogin() {
  try {
    return JSON.parse(sessionStorage.getItem(SAVED_LOGIN_KEY) || "null") || {};
  } catch {
    return {};
  }
}

function newId() {
  return `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

function emptyJoinStep(leftAlias = "A") {
  return {
    id: newId(),
    joinType: "INNER",
    leftAlias,
    rightTable: "",
    leftCol: "",
    rightCol: "",
    related: [],
    relatedLoading: false,
  };
}

function emptyWhere() {
  return {
    id: newId(),
    alias: "A",
    column: "",
    op: "=",
    value: "",
    logic: "AND",
  };
}

function resolveEngine(connection, formEngine, savedEngine) {
  const raw =
    connection?.engine || formEngine || savedEngine || ENGINE_MSSQL;
  return String(raw).toLowerCase() === ENGINE_MYSQL
    ? ENGINE_MYSQL
    : ENGINE_MSSQL;
}

function SqlStudio() {
  const saved = loadSavedLogin();
  const [form, setForm] = useState({
    engine: saved.engine || ENGINE_MSSQL,
    server: saved.server || "localhost",
    port: saved.port || defaultPort(saved.engine || ENGINE_MSSQL),
    user: saved.user || defaultUser(saved.engine || ENGINE_MSSQL),
    password: "",
    encrypt: saved.encrypt ?? true,
    trustServerCertificate: saved.trustServerCertificate ?? true,
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [connected, setConnected] = useState(hasSqlSession());
  const [connection, setConnection] = useState(getSavedSqlConnection());
  const [bridgeOk, setBridgeOk] = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [queryRunning, setQueryRunning] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const resultsRef = useRef(null);
  const queryAbortRef = useRef(null);

  const [databases, setDatabases] = useState([]);
  const [tables, setTables] = useState([]);
  const [tableFilter, setTableFilter] = useState("");
  const [selectedTable, setSelectedTable] = useState(null);

  const [mode, setMode] = useState("browse");
  const [sql, setSql] = useState("");
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchCol, setSearchCol] = useState("");
  const [searchText, setSearchText] = useState("");
  const [rowLimit, setRowLimit] = useState(ROW_LIMIT_ALL);

  // Multi-join builder
  const [baseTable, setBaseTable] = useState("");
  const [joinSteps, setJoinSteps] = useState([]);
  const [columnsCache, setColumnsCache] = useState({});
  const [useWhere, setUseWhere] = useState(false);
  const [whereRules, setWhereRules] = useState([emptyWhere()]);

  const engine = resolveEngine(connection, form.engine, saved.engine);

  const expireSession = useCallback((message = "Session expired. Please connect again.") => {
    clearSqlSession();
    setConnected(false);
    setConnection(null);
    setDatabases([]);
    setTables([]);
    setSelectedTable(null);
    setResult(null);
    setBusy(false);
    setError(message);
    setStatus("");
  }, []);

  const applyApiError = useCallback(
    (err, fallback = "Request failed.") => {
      if (isSqlSessionExpiredError(err)) {
        expireSession();
        return;
      }
      setError(err.response?.data?.message || err.message || fallback);
    },
    [expireSession]
  );

  const onlineDatabases = useMemo(
    () => databases.filter((d) => !d.state || d.state === "ONLINE"),
    [databases]
  );

  const filteredTables = useMemo(() => {
    const q = tableFilter.trim().toLowerCase();
    if (!q) return tables;
    return tables.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.schema.toLowerCase().includes(q)
    );
  }, [tables, tableFilter]);

  const pageRows = useMemo(() => {
    if (!result?.rows?.length) return [];
    const start = (page - 1) * pageSize;
    return result.rows.slice(start, start + pageSize);
  }, [result, page, pageSize]);

  const resultColumns = result?.columns || [];

  const tableByKey = useCallback(
    (key) => tables.find((t) => t.fullName === key) || null,
    [tables]
  );

  /** Alias map: A -> table fullName */
  const aliasMap = useMemo(() => {
    const map = {};
    if (baseTable) map.A = baseTable;
    joinSteps.forEach((step, idx) => {
      if (step.rightTable) map[ALIASES[idx + 1]] = step.rightTable;
    });
    return map;
  }, [baseTable, joinSteps]);

  const aliasList = useMemo(() => Object.keys(aliasMap), [aliasMap]);

  const getCols = useCallback(
    async (fullName) => {
      if (!fullName) return [];
      if (columnsCache[fullName]) return columnsCache[fullName];
      const t = tableByKey(fullName);
      if (!t) return [];
      const data = await sqlColumns(t.schema, t.name);
      const cols = data.columns || [];
      setColumnsCache((prev) => ({ ...prev, [fullName]: cols }));
      return cols;
    },
    [columnsCache, tableByKey]
  );

  const checkBridge = useCallback(async () => {
    try {
      await sqlHealth();
      setBridgeOk(true);
    } catch {
      setBridgeOk(false);
    }
  }, []);

  const loadTables = useCallback(async () => {
    const data = await sqlTables();
    setTables(data.tables || []);
    setConnection(data.connection || getSavedSqlConnection());
    return data.tables || [];
  }, []);

  const stopQuery = useCallback(() => {
    try {
      queryAbortRef.current?.abort();
    } catch {
      /* ignore */
    }
    queryAbortRef.current = null;
    sqlCancel().catch(() => {});
    setStatus("Stopping query…");
  }, []);

  const runSql = useCallback(async (query, successMsg = "") => {
    if (queryAbortRef.current) {
      try {
        queryAbortRef.current.abort();
      } catch {
        /* ignore */
      }
    }
    const ac = new AbortController();
    queryAbortRef.current = ac;

    setBusy(true);
    setQueryRunning(true);
    setError("");
    setStatus(successMsg || "Running query…");
    setPage(1);
    setSql(query);
    try {
      const data = await sqlQuery(query, fetchMaxRows(rowLimit), {
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      setResult(data);
      if (data.columns?.length) setSearchCol(data.columns[0]);
      if (data.truncated) {
        setStatus(
          `${successMsg || "Query done"} · showing first ${data.rows?.length || 0} rows (fetch limit)`
        );
      } else if (successMsg) {
        setStatus(`${successMsg} · ${data.rows?.length || 0} row(s)`);
      } else {
        setStatus(`Done · ${data.rows?.length || 0} row(s)`);
      }
      requestAnimationFrame(() => {
        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      });
    } catch (err) {
      if (isSqlQueryCanceledError(err)) {
        setError("");
        setStatus("Query stopped.");
        return;
      }
      setResult(null);
      applyApiError(err, "Query failed.");
    } finally {
      if (queryAbortRef.current === ac) queryAbortRef.current = null;
      setQueryRunning(false);
      setBusy(false);
    }
  }, [rowLimit, applyApiError]);

  const downloadCsv = () => {
    if (!result?.rows?.length) return;
    const cols = result.columns || [];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [
      cols.map(escape).join(","),
      ...result.rows.map((row) => cols.map((c) => escape(row[c])).join(",")),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sql_studio_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadExcel = () => {
    if (!result?.rows?.length) return;
    const cols = result.columns || [];
    const sheetRows = result.rows.map((row) => {
      const out = {};
      cols.forEach((c) => {
        out[c] = row[c] === null || row[c] === undefined ? "" : row[c];
      });
      return out;
    });
    const worksheet = XLSX.utils.json_to_sheet(sheetRows, { header: cols });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Results");
    XLSX.writeFile(
      workbook,
      `sql_studio_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.xlsx`
    );
  };

  useEffect(() => {
    checkBridge();
    if (!hasSqlSession()) return;
    (async () => {
      try {
        setBusy(true);
        const data = await sqlDatabases();
        setDatabases(data.databases || []);
        setConnected(true);
        setConnection(data.connection || getSavedSqlConnection());
        await loadTables();
        setStatus("Connected. Database selection is optional — pick one when ready.");
      } catch (err) {
        if (isSqlSessionExpiredError(err)) {
          expireSession(
            err.response?.data?.message ||
              "Session expired. Please connect again."
          );
        } else {
          setError(
            err.response?.data?.message ||
              "Unable to restore SQL session. Please connect again."
          );
          if (!hasSqlSession()) {
            expireSession();
          }
        }
      } finally {
        setBusy(false);
      }
    })();
  }, [checkBridge, loadTables, expireSession]);

  // Prefetch base table columns
  useEffect(() => {
    if (baseTable) getCols(baseTable);
  }, [baseTable, getCols]);

  const loadRelatedForAlias = useCallback(
    async (leftAlias, stepId) => {
      const leftTable =
        leftAlias === "A"
          ? baseTable
          : (() => {
              const idx = ALIASES.indexOf(leftAlias) - 1;
              return idx >= 0 ? joinSteps[idx]?.rightTable : "";
            })();

      if (!leftTable) {
        setJoinSteps((prev) =>
          prev.map((s) =>
            s.id === stepId
              ? { ...s, related: [], relatedLoading: false, rightTable: "", leftCol: "", rightCol: "" }
              : s
          )
        );
        return;
      }

      setJoinSteps((prev) =>
        prev.map((s) => (s.id === stepId ? { ...s, relatedLoading: true } : s))
      );

      try {
        const t = tableByKey(leftTable);
        if (!t) {
          setJoinSteps((prev) =>
            prev.map((s) =>
              s.id === stepId
                ? { ...s, related: [], relatedLoading: false, rightTable: "", leftCol: "", rightCol: "" }
                : s
            )
          );
          return;
        }
        const data = await sqlRelatedTables(t.schema, t.name);
        const related = data.related || [];
        setJoinSteps((prev) =>
          prev.map((s) => {
            if (s.id !== stepId) return s;
            const stillValid = related.some((r) => r.fullName === s.rightTable);
            return {
              ...s,
              related,
              relatedLoading: false,
              ...(stillValid
                ? {}
                : { rightTable: "", leftCol: "", rightCol: "" }),
            };
          })
        );
      } catch (err) {
        setJoinSteps((prev) =>
          prev.map((s) =>
            s.id === stepId ? { ...s, related: [], relatedLoading: false } : s
          )
        );
        if (isSqlSessionExpiredError(err)) expireSession();
      }
    },
    [baseTable, joinSteps, tableByKey, expireSession]
  );

  const onChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const setEngine = (nextEngine) => {
    setForm((prev) => {
      const engineChanged = prev.engine !== nextEngine;
      return {
        ...prev,
        engine: nextEngine,
        port: engineChanged ? defaultPort(nextEngine) : prev.port,
        user:
          engineChanged &&
          (prev.user === defaultUser(prev.engine) || !prev.user)
            ? defaultUser(nextEngine)
            : prev.user,
      };
    });
  };

  const resetJoinBuilder = () => {
    setBaseTable("");
    setJoinSteps([]);
    setUseWhere(false);
    setWhereRules([emptyWhere()]);
  };

  const handleConnect = async (e) => {
    e.preventDefault();
    setConnectLoading(true);
    setError("");
    setResult(null);
    setTables([]);
    setSelectedTable(null);
    resetJoinBuilder();
    try {
      const data = await sqlConnect({
        engine: form.engine,
        server: form.server,
        port: Number(form.port) || Number(defaultPort(form.engine)),
        user: form.user,
        password: form.password,
        encrypt: form.encrypt,
        trustServerCertificate: form.trustServerCertificate,
      });
      sessionStorage.setItem(
        SAVED_LOGIN_KEY,
        JSON.stringify({
          engine: form.engine,
          server: form.server,
          port: form.port,
          user: form.user,
          encrypt: form.encrypt,
          trustServerCertificate: form.trustServerCertificate,
        })
      );
      setConnected(true);
      setConnection(data.connection);
      setDatabases(data.databases || []);
      setBridgeOk(true);
      await loadTables();
      setStatus(
        `Connected to ${engineLabel(form.engine)}. Database is optional — select one to browse tables.`
      );
      setMode("browse");
    } catch (err) {
      setConnected(false);
      setError(err.response?.data?.message || err.message || "Connection failed.");
      if (!err.response) setBridgeOk(false);
    } finally {
      setConnectLoading(false);
    }
  };

  const handleDisconnect = async () => {
    await sqlDisconnect();
    setConnected(false);
    setConnection(null);
    setDatabases([]);
    setTables([]);
    setSelectedTable(null);
    setResult(null);
    setError("");
    setStatus("");
    resetJoinBuilder();
  };

  const handleSelectDatabase = async (dbName) => {
    if (!dbName) return;
    setError("");
    setSelectedTable(null);
    setResult(null);
    resetJoinBuilder();
    setBusy(true);
    try {
      const data = await sqlUseDatabase(dbName);
      setConnection(data.connection);
      setDatabases(data.databases || databases);
      const list = await loadTables();
      setStatus(`Database “${dbName}” · ${list.length} tables`);
      setMode("browse");
    } catch (err) {
      applyApiError(err, "Unable to open database.");
    } finally {
      setBusy(false);
    }
  };

  const handleSelectTable = async (table) => {
    setMode("browse");
    setSelectedTable(table);
    setSearchText("");
    const query = buildSelect(
      "*",
      `FROM ${qName(table.schema, table.name, engine)}`,
      rowLimit,
      engine
    );
    await runSql(query, `Previewing ${table.fullName}`);
  };

  const handleEasySearch = async () => {
    if (!selectedTable || !searchCol || !searchText.trim()) {
      setError("Select a table, choose a column, then enter search text.");
      return;
    }
    const col = qIdent(searchCol, engine);
    const where = `WHERE ${likeExpr(col, searchText, engine)}`;
    const query = buildSelect(
      "*",
      `FROM ${qName(selectedTable.schema, selectedTable.name, engine)}\n${where}`,
      rowLimit,
      engine
    );
    await runSql(query, `Filtered by ${searchCol}`);
  };

  const handleCount = async () => {
    if (!selectedTable) return;
    const q = `SELECT COUNT(*) AS TotalRows FROM ${qName(selectedTable.schema, selectedTable.name, engine)};`;
    await runSql(q, `Row count for ${selectedTable.name}`);
  };

  const setBase = async (fullName) => {
    setBaseTable(fullName);
    setJoinSteps([]);
    setWhereRules([emptyWhere()]);
    if (fullName) await getCols(fullName);
  };

  // Prefetch columns used in WHERE
  useEffect(() => {
    if (!useWhere) return;
    whereRules.forEach((r) => {
      const t = aliasMap[r.alias];
      if (t) getCols(t);
    });
  }, [useWhere, whereRules, aliasMap, getCols]);

  const updateStep = (id, patch) => {
    setJoinSteps((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  };

  const changeLeftAlias = async (stepId, leftAlias) => {
    updateStep(stepId, {
      leftAlias,
      rightTable: "",
      leftCol: "",
      rightCol: "",
      related: [],
    });
    await loadRelatedForAlias(leftAlias, stepId);
  };

  const pickRightTable = async (stepId, fullName) => {
    const step = joinSteps.find((s) => s.id === stepId);
    if (!step) return;
    if (!fullName) {
      updateStep(stepId, { rightTable: "", leftCol: "", rightCol: "" });
      return;
    }
    const related = step.related.find((r) => r.fullName === fullName);
    const fk = related?.joinKeys?.[0];
    updateStep(stepId, {
      rightTable: fullName,
      leftCol: fk?.columnA || "",
      rightCol: fk?.columnB || "",
    });
    await getCols(fullName);
  };

  const addJoinStep = async () => {
    if (!baseTable) {
      setError("Select a base table first.");
      return;
    }
    if (aliasList.length >= ALIASES.length) {
      setError("Maximum join limit reached.");
      return;
    }
    const lastAlias = aliasList[aliasList.length - 1] || "A";
    const step = emptyJoinStep(lastAlias);
    setJoinSteps((prev) => [...prev, step]);
    await loadRelatedForAlias(lastAlias, step.id);
  };

  const removeJoinStep = (id) => {
    setJoinSteps((prev) => prev.filter((s) => s.id !== id));
  };

  const buildWhereSql = () => {
    if (!useWhere) return "";
    const parts = [];
    whereRules.forEach((rule) => {
      if (!rule.column || !rule.alias) return;
      if (!["IS NULL", "IS NOT NULL"].includes(rule.op) && rule.value === "") return;

      let expr = qCol(rule.alias, rule.column, engine);
      if (rule.op === "IS NULL" || rule.op === "IS NOT NULL") {
        expr = `${expr} ${rule.op}`;
      } else if (rule.op === "LIKE") {
        expr = likeExpr(expr, rule.value, engine);
      } else {
        const numeric = /^-?\d+(\.\d+)?$/.test(String(rule.value).trim());
        expr = numeric
          ? `${expr} ${rule.op} ${String(rule.value).trim()}`
          : `${expr} ${rule.op} ${litString(rule.value, engine)}`;
      }

      if (parts.length === 0) parts.push(expr);
      else parts.push(`${rule.logic || "AND"} ${expr}`);
    });
    return parts.length ? `WHERE ${parts.join(" ")}` : "";
  };

  const runJoin = async () => {
    const base = tableByKey(baseTable);
    if (!base) {
      setError("Select a base table first.");
      return;
    }

    const validSteps = joinSteps.filter(
      (s) => s.rightTable && s.leftCol && s.rightCol && aliasMap[s.leftAlias]
    );

    const usedAliases = ["A", ...validSteps.map((_, idx) => ALIASES[idx + 1])];
    const selectParts = usedAliases.map((a) => `${a}.*`).join(",\n  ");
    let fromSql = `FROM ${qName(base.schema, base.name, engine)} AS A`;

    validSteps.forEach((step, idx) => {
      const rightAlias = ALIASES[idx + 1];
      const right = tableByKey(step.rightTable);
      if (!right) return;
      fromSql += `
${step.joinType} JOIN ${qName(right.schema, right.name, engine)} AS ${rightAlias}
  ON ${qCol(step.leftAlias, step.leftCol, engine)} = ${qCol(rightAlias, step.rightCol, engine)}`;
    });

    const whereSql = buildWhereSql();
    const body = `${fromSql}${whereSql ? `\n${whereSql}` : ""}`;
    const query = buildSelect(selectParts, body, rowLimit, engine);

    setSelectedTable(null);
    await runSql(
      query,
      validSteps.length
        ? `Query · ${validSteps.length} join(s)${whereSql ? " · WHERE" : ""}`
        : `Query · ${base.name}${whereSql ? " · WHERE" : ""}`
    );
  };

  const previewJoinSql = useMemo(() => {
    const base = tableByKey(baseTable);
    if (!base) return "";
    const validSteps = joinSteps.filter(
      (s) => s.rightTable && s.leftCol && s.rightCol && aliasMap[s.leftAlias]
    );
    const usedAliases = ["A", ...validSteps.map((_, idx) => ALIASES[idx + 1])];
    const selectParts = usedAliases.map((a) => `${a}.*`).join(", ");
    let fromSql = `FROM ${qName(base.schema, base.name, engine)} AS A`;
    validSteps.forEach((step, idx) => {
      const rightAlias = ALIASES[idx + 1];
      const right = tableByKey(step.rightTable);
      if (!right) return;
      fromSql += ` ${step.joinType} JOIN ${qName(right.schema, right.name, engine)} AS ${rightAlias} ON ${qCol(step.leftAlias, step.leftCol, engine)} = ${qCol(rightAlias, step.rightCol, engine)}`;
    });
    const whereSql = buildWhereSql();
    const body = `${fromSql}${whereSql ? ` ${whereSql}` : ""}`;
    return buildSelect(selectParts, body, rowLimit, engine);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTable, joinSteps, aliasMap, useWhere, whereRules, rowLimit, tables, engine]);

  return (
    <MainLayout>
      <div className="sql-studio">
        <div className="sql-head">
          <div>
            <h3 className="page-title">SQL Studio</h3>
            <p className="page-subtitle">
              Connect to MS SQL or MySQL — browse tables, build joins, filter, and export
            </p>
          </div>
          <div className={`sql-conn-chip ${connected ? "" : "offline"}`}>
            {connected && connection
              ? `${engineLabel(connection.engine)} · ${connection.server}${connection.database ? ` · ${connection.database}` : ""}`
              : bridgeOk === false
                ? isLocalSqlBridgeHost()
                  ? "Bridge offline — run npm run sql-server"
                  : "SQL bridge offline on live"
                : "Disconnected"}
          </div>
        </div>

        {!connected ? (
          <form className="sql-connect-card sql-easy-connect" onSubmit={handleConnect}>
            <h4 className="sql-easy-title">Server connection</h4>
            <p className="sql-easy-help">
              Choose MS SQL or MySQL. Database is optional — connect first, then pick a database from the list.
            </p>

            {bridgeOk === false ? (
              <div className="sql-error">
                {isLocalSqlBridgeHost() ? (
                  <>
                    SQL bridge is offline. Start it with{" "}
                    <code>npm run sql-server</code>
                  </>
                ) : (
                  <>
                    SQL bridge live pe offline hai (
                    <code>{SQL_API_URL}</code>). Latest code{" "}
                    <code>fahad</code> branch pe push karke GitHub deploy
                    complete hone do, phir{" "}
                    <a
                      href={`${SQL_API_URL}/api/sql/health`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      health check
                    </a>{" "}
                    open karke verify karo. Details:{" "}
                    <code>sql-studio-bridge/LIVE.md</code>
                  </>
                )}
              </div>
            ) : null}

            <div className="sql-engine-tabs" role="tablist" aria-label="Database engine">
              <button
                type="button"
                className={form.engine === ENGINE_MSSQL ? "active" : ""}
                onClick={() => setEngine(ENGINE_MSSQL)}
              >
                MS SQL Server
              </button>
              <button
                type="button"
                className={form.engine === ENGINE_MYSQL ? "active" : ""}
                onClick={() => setEngine(ENGINE_MYSQL)}
              >
                MySQL
              </button>
            </div>

            <div className="sql-connect-grid">
              <div>
                <label className="form-label fw-semibold">
                  {isMysql(form.engine) ? "Host" : "Server"}
                </label>
                <input
                  className="form-control"
                  value={form.server}
                  onChange={(e) => onChange("server", e.target.value)}
                  placeholder={
                    isMysql(form.engine)
                      ? "localhost or IP"
                      : "localhost or host\\instance"
                  }
                  required
                />
              </div>
              <div>
                <label className="form-label fw-semibold">Username</label>
                <input
                  className="form-control"
                  value={form.user}
                  onChange={(e) => onChange("user", e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="form-label fw-semibold">Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={form.password}
                  onChange={(e) => onChange("password", e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            <button
              type="button"
              className="sql-link-btn"
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? "Hide advanced options" : "Show advanced options"}
            </button>

            {showAdvanced ? (
              <div className="sql-connect-grid mt-2">
                <div>
                  <label className="form-label fw-semibold">Port</label>
                  <input
                    className="form-control"
                    value={form.port}
                    onChange={(e) => onChange("port", e.target.value)}
                    placeholder={defaultPort(form.engine)}
                  />
                </div>
                {!isMysql(form.engine) ? (
                  <div className="full sql-checks">
                    <label>
                      <input
                        type="checkbox"
                        checked={form.encrypt}
                        onChange={(e) => onChange("encrypt", e.target.checked)}
                      />
                      Encrypt connection
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={form.trustServerCertificate}
                        onChange={(e) =>
                          onChange("trustServerCertificate", e.target.checked)
                        }
                      />
                      Trust server certificate
                    </label>
                  </div>
                ) : null}
              </div>
            ) : null}

            {error ? <div className="sql-error mt-3">{error}</div> : null}

            <div className="sql-actions">
              <button
                className="btn btn-primary-custom px-4"
                type="submit"
                disabled={connectLoading}
              >
                {connectLoading ? <ButtonLoader label="Connecting…" /> : "Connect"}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div className="sql-top-pick">
              <label>
                Database <span className="sql-optional">(optional)</span>
                <SearchSelect
                  value={connection?.database || ""}
                  onChange={(v) => v && handleSelectDatabase(v)}
                  placeholder={
                    connection?.database
                      ? `Current: ${connection.database}`
                      : "Search / select database…"
                  }
                  options={onlineDatabases.map((db) => ({
                    value: db.name,
                    label: db.name,
                  }))}
                />
              </label>
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleDisconnect}
              >
                Disconnect
              </button>
            </div>

            <div className="sql-mode-tabs">
              <button
                type="button"
                className={mode === "browse" ? "active" : ""}
                onClick={() => setMode("browse")}
              >
                Browse
              </button>
              <button
                type="button"
                className={mode === "join" ? "active" : ""}
                onClick={() => setMode("join")}
              >
                Join Builder
              </button>
              <button
                type="button"
                className={mode === "sql" ? "active" : ""}
                onClick={() => setMode("sql")}
              >
                SQL Editor
              </button>
            </div>

            <div className="sql-workbench sql-easy-workbench">
              <aside className="sql-side">
                <div className="sql-side-head">
                  <h4>Tables</h4>
                  <span className="sql-side-count">{filteredTables.length}</span>
                </div>
                <div className="sql-side-filter">
                  <input
                    className="form-control form-control-sm"
                    placeholder="Filter tables…"
                    value={tableFilter}
                    onChange={(e) => setTableFilter(e.target.value)}
                  />
                </div>
                {busy && !tables.length ? (
                  <PageLoader label="Loading tables…" />
                ) : (
                  <ul className="sql-table-list">
                    {filteredTables.length === 0 ? (
                      <li className="sql-empty">Select a database to load tables</li>
                    ) : (
                      filteredTables.map((t) => (
                        <li key={t.fullName}>
                          <button
                            type="button"
                            className={
                              selectedTable?.fullName === t.fullName ||
                              baseTable === t.fullName
                                ? "active"
                                : ""
                            }
                            onClick={() => {
                              if (mode === "join") setBase(t.fullName);
                              else handleSelectTable(t);
                            }}
                          >
                            <span className="schema">{t.schema}</span>
                            {t.name}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                )}
              </aside>

              <section className="sql-main">
                {mode === "join" ? (
                  <div className="sql-join-card">
                    <h4>Query Builder</h4>
                    <p className="sql-easy-help">
                      Select a base table to run. Joins are optional — add only if needed.
                      Related-table join keys are applied automatically.
                    </p>

                    <div className="sql-join-base">
                      <label>
                        Base table (A)
                        <SearchSelect
                          value={baseTable}
                          onChange={(v) => setBase(v)}
                          placeholder="Search / select base table…"
                          options={tables.map((t) => ({
                            value: t.fullName,
                            label: t.fullName,
                            searchText: `${t.schema} ${t.name}`,
                          }))}
                        />
                      </label>
                    </div>

                    {joinSteps.map((step, idx) => {
                      const rightAlias = ALIASES[idx + 1];
                      return (
                        <div className="sql-join-step" key={step.id}>
                          <div className="sql-join-step-head">
                            <strong>
                              Join {idx + 1} → {rightAlias}
                              <span className="sql-optional"> (optional)</span>
                            </strong>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeJoinStep(step.id)}
                            >
                              Remove
                            </button>
                          </div>

                          <div className="sql-join-step-grid sql-join-step-grid-3">
                            <div>
                              <label>Join type</label>
                              <SearchSelect
                                value={step.joinType}
                                onChange={(v) =>
                                  updateStep(step.id, { joinType: v || "INNER" })
                                }
                                placeholder="Join type…"
                                options={JOIN_TYPES.map((j) => ({
                                  value: j.id,
                                  label: j.label,
                                  searchText: j.help,
                                }))}
                              />
                            </div>
                            <div>
                              <label>Join from</label>
                              <SearchSelect
                                value={step.leftAlias}
                                onChange={(v) =>
                                  v && changeLeftAlias(step.id, v)
                                }
                                disabled={!baseTable}
                                placeholder="Join from…"
                                options={aliasList
                                  .filter((a) => ALIASES.indexOf(a) <= idx)
                                  .map((a) => ({
                                    value: a,
                                    label: `${a} · ${aliasMap[a] || ""}`,
                                    searchText: `${a} ${aliasMap[a] || ""}`,
                                  }))}
                              />
                            </div>
                            <div>
                              <label>
                                Related table ({rightAlias})
                                <span className="sql-fk-badge">
                                  {step.relatedLoading
                                    ? "Loading…"
                                    : `${step.related.length} FK`}
                                </span>
                              </label>
                              <SearchSelect
                                value={step.rightTable}
                                onChange={(v) => pickRightTable(step.id, v)}
                                disabled={
                                  !baseTable ||
                                  step.relatedLoading ||
                                  step.related.length === 0
                                }
                                placeholder={
                                  !baseTable
                                    ? "Select base table first…"
                                    : step.relatedLoading
                                      ? "Loading related…"
                                      : step.related.length === 0
                                        ? "No FK-related tables"
                                        : "Search related table…"
                                }
                                options={step.related.map((t) => ({
                                  value: t.fullName,
                                  label: `${t.fullName}${t.fkName ? ` · ${t.fkName}` : ""}`,
                                  searchText: `${t.fullName} ${t.fkName || ""} ${t.columnA || ""} ${t.columnB || ""}`,
                                }))}
                              />
                            </div>
                          </div>

                          {step.rightTable && step.leftCol && step.rightCol ? (
                            <div className="sql-on-auto">
                              ON auto:
                              <code>
                                {qCol(step.leftAlias, step.leftCol, engine)} ={" "}
                                {qCol(rightAlias, step.rightCol, engine)}
                              </code>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}

                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm mt-2"
                      onClick={addJoinStep}
                      disabled={
                        !baseTable ||
                        (joinSteps.length > 0 &&
                          !joinSteps[joinSteps.length - 1]?.rightTable)
                      }
                    >
                      + Add join (optional)
                    </button>

                    <div className="sql-where-card">
                      <label className="sql-where-toggle">
                        <input
                          type="checkbox"
                          checked={useWhere}
                          onChange={(e) => setUseWhere(e.target.checked)}
                        />
                        Add WHERE filter <span className="sql-optional">(optional)</span>
                      </label>

                      {useWhere ? (
                        <div className="sql-where-list">
                          {whereRules.map((rule, idx) => {
                            const cols = columnsCache[aliasMap[rule.alias]] || [];
                            return (
                              <div className="sql-where-row" key={rule.id}>
                                {idx > 0 ? (
                                  <SearchSelect
                                    value={rule.logic}
                                    onChange={(v) =>
                                      setWhereRules((prev) =>
                                        prev.map((r) =>
                                          r.id === rule.id
                                            ? { ...r, logic: v || "AND" }
                                            : r
                                        )
                                      )
                                    }
                                    placeholder="Logic"
                                    options={[
                                      { value: "AND", label: "AND" },
                                      { value: "OR", label: "OR" },
                                    ]}
                                  />
                                ) : (
                                  <span className="sql-where-logic-spacer">WHERE</span>
                                )}
                                <SearchSelect
                                  value={rule.alias}
                                  onChange={(v) =>
                                    setWhereRules((prev) =>
                                      prev.map((r) =>
                                        r.id === rule.id
                                          ? { ...r, alias: v || "A", column: "" }
                                          : r
                                      )
                                    )
                                  }
                                  placeholder="Alias"
                                  options={aliasList.map((a) => ({
                                    value: a,
                                    label: a,
                                    searchText: aliasMap[a] || "",
                                  }))}
                                />
                                <SearchSelect
                                  value={rule.column}
                                  onChange={(v) =>
                                    setWhereRules((prev) =>
                                      prev.map((r) =>
                                        r.id === rule.id
                                          ? { ...r, column: v }
                                          : r
                                      )
                                    )
                                  }
                                  placeholder="Search column…"
                                  options={cols.map((c) => ({
                                    value: c.name,
                                    label: c.name,
                                    searchText: c.dataType || "",
                                  }))}
                                />
                                <SearchSelect
                                  value={rule.op}
                                  onChange={(v) =>
                                    setWhereRules((prev) =>
                                      prev.map((r) =>
                                        r.id === rule.id
                                          ? { ...r, op: v || "=" }
                                          : r
                                      )
                                    )
                                  }
                                  placeholder="Operator"
                                  options={WHERE_OPS.map((op) => ({
                                    value: op.id,
                                    label: op.label,
                                  }))}
                                />
                                {!["IS NULL", "IS NOT NULL"].includes(rule.op) ? (
                                  <input
                                    className="form-control form-control-sm"
                                    placeholder="Value"
                                    value={rule.value}
                                    onChange={(e) =>
                                      setWhereRules((prev) =>
                                        prev.map((r) =>
                                          r.id === rule.id
                                            ? { ...r, value: e.target.value }
                                            : r
                                        )
                                      )
                                    }
                                  />
                                ) : (
                                  <span className="sql-where-logic-spacer" />
                                )}
                                {whereRules.length > 1 ? (
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() =>
                                      setWhereRules((prev) =>
                                        prev.filter((r) => r.id !== rule.id)
                                      )
                                    }
                                  >
                                    ×
                                  </button>
                                ) : null}
                              </div>
                            );
                          })}
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() =>
                              setWhereRules((prev) => [
                                ...prev,
                                {
                                  ...emptyWhere(),
                                  alias: aliasList[aliasList.length - 1] || "A",
                                },
                              ])
                            }
                          >
                            + Add condition
                          </button>
                        </div>
                      ) : null}
                    </div>

                    {previewJoinSql ? (
                      <pre className="sql-preview">{previewJoinSql}</pre>
                    ) : null}

                    <div className="sql-run-bar">
                      <div className="sql-limit-wrap">
                        <label className="sql-limit-label" htmlFor="sql-fetch-join">
                          Fetch
                        </label>
                        <select
                          id="sql-fetch-join"
                          className="form-select form-select-sm sql-limit-select"
                          value={String(rowLimit)}
                          onChange={(e) => setRowLimit(e.target.value || ROW_LIMIT_ALL)}
                        >
                          {ROW_LIMIT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary-custom"
                        onClick={runJoin}
                        disabled={busy || !baseTable}
                      >
                        {busy ? <ButtonLoader label="Running…" /> : "Run Query"}
                      </button>
                      {queryRunning ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger"
                          onClick={stopQuery}
                        >
                          Stop
                        </button>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                {mode === "browse" ? (
                  <div className="sql-easy-toolbar">
                    <div className="sql-easy-actions">
                      <div className="sql-limit-wrap">
                        <label className="sql-limit-label" htmlFor="sql-fetch-browse">
                          Fetch
                        </label>
                        <select
                          id="sql-fetch-browse"
                          className="form-select form-select-sm sql-limit-select"
                          value={String(rowLimit)}
                          onChange={(e) => setRowLimit(e.target.value || ROW_LIMIT_ALL)}
                        >
                          {ROW_LIMIT_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary-custom"
                        disabled={!selectedTable || busy}
                        onClick={() => selectedTable && handleSelectTable(selectedTable)}
                      >
                        {busy ? <ButtonLoader label="Loading…" /> : "Load data"}
                      </button>
                      {queryRunning ? (
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={stopQuery}
                        >
                          Stop
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        disabled={!selectedTable || busy}
                        onClick={handleCount}
                      >
                        Count rows
                      </button>
                    </div>

                    <div className="sql-easy-search">
                      <div className="sql-easy-search-col">
                        <SearchSelect
                          value={searchCol}
                          onChange={setSearchCol}
                          disabled={!resultColumns.length}
                          placeholder="Column…"
                          options={resultColumns.map((c) => ({
                            value: c,
                            label: c,
                          }))}
                        />
                      </div>
                      <input
                        className="form-control form-control-sm"
                        placeholder="Filter value…"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleEasySearch()}
                        disabled={!selectedTable}
                      />
                      <button
                        type="button"
                        className="btn btn-sm btn-primary-custom"
                        onClick={handleEasySearch}
                        disabled={!selectedTable || busy}
                      >
                        Filter
                      </button>
                    </div>
                  </div>
                ) : null}

                {mode === "sql" ? (
                  <div className="sql-editor-wrap">
                    <textarea
                      className="sql-editor"
                      value={sql}
                      onChange={(e) => setSql(e.target.value)}
                      spellCheck={false}
                      placeholder="Write SQL here…"
                    />
                    <div className="sql-editor-bar">
                      <div className="left">
                        <div className="sql-limit-wrap">
                          <label className="sql-limit-label" htmlFor="sql-fetch-sql">
                            Fetch
                          </label>
                          <select
                            id="sql-fetch-sql"
                            className="form-select form-select-sm sql-limit-select"
                            value={String(rowLimit)}
                            onChange={(e) => setRowLimit(e.target.value || ROW_LIMIT_ALL)}
                          >
                            {ROW_LIMIT_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="btn btn-primary-custom"
                          disabled={busy || !sql.trim()}
                          onClick={() => runSql(sql, "Query executed")}
                        >
                          {busy ? <ButtonLoader label="Running…" /> : "Execute"}
                        </button>
                        {queryRunning ? (
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={stopQuery}
                          >
                            Stop
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}

                {status ? <div className="sql-hint">{status}</div> : null}
                {error ? <div className="sql-error">{error}</div> : null}

                <div className="sql-results" ref={resultsRef}>
                  {!result && !error ? (
                    <div className="sql-empty sql-empty-big">
                      <p>
                        {mode === "join"
                          ? "Configure base table, add joins, optionally set WHERE, then Run Query."
                          : mode === "sql"
                            ? "Write a query and click Execute."
                            : "Select a table from the left panel to preview data."}
                      </p>
                    </div>
                  ) : null}

                  {result ? (
                    <>
                      <div className="sql-results-meta">
                        <span>
                          Rows: <strong>{result.rowCount}</strong>
                          {result.truncated ? " (limit reached)" : ""}
                        </span>
                        <span>
                          Affected: <strong>{result.rowsAffected}</strong>
                        </span>
                        <span>
                          Time: <strong>{result.elapsedMs} ms</strong>
                        </span>
                        {result.columns?.length ? (
                          <div className="sql-download">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-success"
                              onClick={downloadExcel}
                              disabled={!result.rows?.length}
                              title="Download Excel"
                            >
                              <i className="bi bi-file-earmark-excel me-1" aria-hidden="true" />
                              Excel
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={downloadCsv}
                              disabled={!result.rows?.length}
                              title="Download CSV"
                            >
                              <i className="bi bi-filetype-csv me-1" aria-hidden="true" />
                              CSV
                            </button>
                          </div>
                        ) : null}
                      </div>

                      {result.columns?.length ? (
                        <>
                          <div className="sql-results-body table-responsive">
                            <table className="table wd-table align-middle mb-0">
                              <thead>
                                <tr>
                                  {result.columns.map((col) => (
                                    <th key={col}>{col}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {pageRows.map((row, idx) => (
                                  <tr key={idx}>
                                    {result.columns.map((col) => {
                                      const raw =
                                        row[col] === null || row[col] === undefined
                                          ? "NULL"
                                          : String(row[col]);
                                      return (
                                        <td key={col} title={raw}>
                                          {raw}
                                        </td>
                                      );
                                    })}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <TablePagination
                            page={page}
                            pageSize={pageSize}
                            total={result.rows?.length || 0}
                            onPageChange={setPage}
                            onPageSizeChange={(n) => {
                              setPageSize(n);
                              setPage(1);
                            }}
                            pageSizeOptions={[25, 50, 100, 250, 500]}
                          />
                        </>
                      ) : (
                        <div className="sql-empty">
                          Query completed. No result set returned.
                        </div>
                      )}
                    </>
                  ) : null}
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </MainLayout>
  );
}

export default SqlStudio;
